import { useEffect, useState } from 'react'
import { api, type ConferenceEvent, type StatusEntry } from '../lib/api.ts'
import { checkSides } from '../lib/verificationItems.ts'
import { CHECK_LABEL, STAGE_WHERE, label } from '../lib/vocabulary.ts'
import { Button } from './Button.tsx'
import { icons } from './icons.ts'
import { StatusTimeline, stopAt } from './StatusTimeline.tsx'

/**
 * What happened to this conferência, and who did it.
 *
 * Galdinho asked for this so a pharmacist can see what comercial already
 * changed. A modal rather than a tab: it answers a question that is asked
 * occasionally, and a tab would spend a slot on every conferência to serve it.
 *
 * **The difference comes from the event, not from the finding.** Each decision
 * carries a snapshot of what disagreed at the moment somebody judged it,
 * because `findings` is rewritten on every reverify — a trail that resolved
 * through it would decay into a list of decisions with nothing beside them,
 * which is exactly the history nobody can use.
 *
 * **A table, not a stack of boards.** The review screen shows one difference at
 * a time in depth; this is asked as a question about the whole conferência, so
 * it reads as rows: receita, no sistema, what was decided, by whom, when. Every
 * check lands in the same table -- somebody looking for what changed on an
 * ingredient should not have to know which check produced it.
 */

/** One pass over the conferência: everything decided before it was concluded. */
interface Round {
  readonly id: string
  readonly at: string
  readonly by: string
  readonly outcome: string | undefined
  /** Where the requisition was when it was concluded (#489), if recorded. */
  readonly stage: string | undefined
  readonly decisions: readonly ConferenceEvent[]
  readonly comments: readonly ConferenceEvent[]
  /**
   * Whether this pass was actually concluded.
   *
   * Not derivable from `by`, which falls back to whoever answered first so a
   * pass in progress still names somebody. Only a `verified` event means
   * somebody signed it.
   */
  readonly concluded: boolean
  /**
   * Who sent this pass's corrections back to be verified again (#439).
   *
   * Filed under the round that asked for them, so it reads as the answer to
   * that round rather than as a round of its own with nothing in it.
   */
  readonly returnedBy: string | undefined
}

/**
 * Splits the trail into the passes that produced it.
 *
 * A conferência can be concluded, sent back to comercial, reverified and
 * concluded again — and a reverify clears every decision, because the findings
 * are rewritten wholesale. Flattened, the same difference appears twice with
 * nothing to say which pass each answer belonged to, which is precisely the
 * question somebody opening this is asking.
 */
function rounds(events: readonly ConferenceEvent[]): readonly Round[] {
  const byId = new Map<string, ConferenceEvent[]>()
  for (const event of events) {
    const id = event.detail?.conclusionId ?? 'sem-conclusao'
    byId.set(id, [...(byId.get(id) ?? []), event])
  }

  return [...byId.entries()].map(([id, all]) => {
    const closed = all.find((e) => e.kind === 'verified')
    const decisions = all.filter((e) => e.kind === 'decision')
    return {
      id,
      at: closed?.at ?? all[all.length - 1]?.at ?? '',
      by: closed?.actor_email ?? decisions[0]?.actor_email ?? '',
      outcome: closed?.detail?.outcome,
      stage: closed?.stage ?? undefined,
      concluded: closed !== undefined,
      decisions,
      /*
       * Kept beside the decisions rather than among them.
       *
       * A comment is not an answer to a difference -- it has no fingerprint,
       * no decision and no check -- so listing it in the same table would put
       * an empty row under three columns it can never fill.
       */
      comments: all.filter((e) => e.kind === 'feedback'),
      returnedBy: all.find((e) => e.kind === 'reverification_requested')?.actor_email,
    }
  })
}

/** The decision, said the way the buttons say it, and coloured the way they are. */
const VERDICT_ACTION: Readonly<Record<string, { text: string; tone: string }>> = {
  corrected: { text: 'Corrigir no sistema', tone: 'text-text-success' },
  accepted: { text: 'Não é divergência', tone: 'text-text-error' },
  rejected: { text: 'Não procede', tone: 'text-text-weak' },
}

const TH = 'text-tSmall text-text-weak px-3 py-2 text-left font-normal uppercase tracking-[0.04em]'
const TD = 'border-t border-stroke-weak px-3 py-2 align-top text-tP text-text-strong'

export function ConferenceHistory({
  verificationId,
  onClose,
}: {
  verificationId: string
  onClose: () => void
}) {
  const [events, setEvents] = useState<readonly ConferenceEvent[] | undefined>()
  const [statuses, setStates] = useState<readonly StatusEntry[]>([])
  const [error, setError] = useState<string | undefined>()
  // The moment under the pointer, so the timeline can light up its stop.
  const [hoveredAt, setHoveredAt] = useState<string | undefined>()

  useEffect(() => {
    let live = true
    void api
      .conferenceHistory(verificationId)
      .then((answer) => {
        if (live) {
          setEvents(answer.events)
          setStates(answer.statuses ?? [])
        }
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : 'falha ao ler o histórico')
      })
    return () => {
      live = false
    }
  }, [verificationId])

  const passes = events === undefined ? [] : rounds(events)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Histórico da conferência"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5"
      onClick={onClose}
    >
      <div
        className="bg-background-raised flex max-h-full w-full max-w-4xl flex-col rounded-[12px] shadow-lg"
        // The backdrop closes; the panel must not, or every click inside it
        // dismisses the thing the person is reading.
        onClick={(event) => event.stopPropagation()}
      >
        <header className="border-stroke-weak flex items-center gap-3 border-b p-5">
          <h2 className="text-tH5 text-text-strong flex-1">Histórico da conferência</h2>
          <Button
            size="icon"
            variant="tertiary"
            tone="neutral"
            leadingIcon={icons.close}
            aria-label="Fechar"
            onClick={onClose}
          />
        </header>

        <div className="flex min-h-0 flex-col gap-6 overflow-y-auto p-5">
          {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

          {events === undefined && error === undefined && (
            <p className="text-tP text-text-weak">Carregando…</p>
          )}

          {/* Where it has been, before what was decided: the statuses it passed
              through and when, oldest first. */}
          {statuses.length > 0 && (
            <StatusTimeline statuses={statuses} highlighted={stopAt(statuses, hoveredAt)} />
          )}

          {events?.length === 0 && (
            <p className="text-tP text-text-weak">
              Nada decidido ainda. As decisões aparecem quando a primeira conferência é concluída.
            </p>
          )}

          {passes.map((pass, index) => (
            <PassTable key={pass.id} pass={pass} number={index + 1} onHover={setHoveredAt} />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * One completed pass, as a table.
 *
 * A row per decision, reading left to right the way somebody asks the
 * question: what did the receita say, what did the sistema say, what was
 * decided, by whom, when. Every check lands in the same table -- a
 * pharmacist looking for what changed on an ingredient should not have to know
 * which check produced it.
 *
 * A reverify rewrites every finding and clears every decision, so the next
 * conclusion becomes its own pass. The earlier ones stay exactly as they were,
 * which is why the difference is copied into each row rather than pointed at.
 */
function PassTable({
  pass,
  number,
  onHover,
}: {
  pass: Round
  number: number
  /** The moment under the pointer, or nothing when it leaves. */
  onHover: (at: string | undefined) => void
}) {
  // The pass happened in the status its first decision was taken in; the
  // conclusion's own moment may already be the next status's.
  const happenedAt = pass.decisions[0]?.at ?? pass.at
  return (
    <section className="flex flex-col gap-2">
      <header
        className="flex flex-wrap items-baseline gap-x-3"
        onMouseEnter={() => onHover(happenedAt)}
        onMouseLeave={() => onHover(undefined)}
      >
        <h3 className="text-tH6 text-text-strong">Conferência #{number}</h3>
        <span className="text-tSmall text-text-weak">
          {pass.by === '' ? 'em andamento' : `concluída por ${pass.by}`}
          {pass.stage === undefined ? '' : ` ${label(STAGE_WHERE, pass.stage)}`}
          {pass.at === '' ? '' : ` · ${when(pass.at)}`}
          {pass.returnedBy === undefined ? '' : ` · corrigida por ${pass.returnedBy}`}
        </span>
      </header>

      {pass.decisions.length === 0 ? (
        /*
         * A pass still being worked has answered nothing *yet*, which is not
         * the same statement as having had nothing to answer. The header above
         * already reads "em andamento", and this line under it was saying the
         * conferência was concluded at the same time.
         */
        <p className="text-tP text-text-weak">
          {pass.concluded
            ? 'Concluída sem apontamentos a responder.'
            : 'Nada respondido ainda nesta passagem.'}
        </p>
      ) : (
        <div className="border-stroke-weak overflow-x-auto rounded-[8px] border">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {/*
                  What was checked, in its own column.
                  
                  It was a caption under the receita value, which put two
                  different things -- what the prescription said, and which
                  check produced the row -- in one cell and made both harder to
                  scan. "Verificação" rather than "dimensão", which is our word
                  for it and nobody else's, and rather than "item", which
                  already means a preparation here.
                */}
                <th className={TH}>Verificação</th>
                <th className={TH}>Receita</th>
                <th className={TH}>No sistema</th>
                <th className={TH}>Ação</th>
                <th className={TH}>Usuário</th>
                <th className={TH}>Data</th>
              </tr>
            </thead>
            <tbody>
              {pass.decisions.map((event, index) => (
                <DecisionRow key={index} event={event} onHover={onHover} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*
        Comments last, and in their own block.
        
        A comment answers no difference -- no fingerprint, no decision, no
        check -- so putting one in the table above would draw an empty row
        under three columns it can never fill. It is also the only thing a
        conferência with nothing to answer can carry, which is exactly when the
        table above says "concluída sem apontamentos".
      */}
      {pass.comments.length > 0 && (
        <div className="mt-3 flex flex-col gap-2">
          {pass.comments.map((comment, index) => (
            <div
              key={index}
              className="rounded-base border-stroke-weak bg-background-sunken border p-2"
              onMouseEnter={() => onHover(comment.at)}
              onMouseLeave={() => onHover(undefined)}
            >
              <span className="text-tTiny text-text-weak mb-1 block">
                {comment.actor_email}
                {comment.at === '' ? '' : ` · ${when(comment.at)}`}
              </span>
              <span className="text-tP text-text-strong break-words whitespace-pre-wrap">
                {comment.notes ?? ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function DecisionRow({
  event,
  onHover,
}: {
  event: ConferenceEvent
  onHover: (at: string | undefined) => void
}) {
  const detail = event.detail ?? {}
  /*
   * Read the way the review screen reads it.
   *
   * Each check names its sides differently and this is the one place that
   * knows all of them. Picking two fields at write time meant picking wrongly
   * for most checks, and an ingredient row rendered as two dashes.
   */
  const sides = checkSides({
    check: detail.check ?? '',
    evidence: detail.evidence ?? {},
  } as never)
  const action = event.decision === null ? undefined : VERDICT_ACTION[event.decision]

  return (
    <tr
      className="hover:bg-background-sunken"
      onMouseEnter={() => onHover(event.at)}
      onMouseLeave={() => onHover(undefined)}
    >
      {/* The same ingredient can be raised by identity and by dose, so two
          rows that look alike need to say what each one was. */}
      <td className={`${TD} whitespace-nowrap`}>{label(CHECK_LABEL, detail.check ?? '')}</td>
      <td className={TD}>
        <Side text={sides.prescribed} />
      </td>
      <td className={TD}>
        <Side text={sides.quoted} />
      </td>
      <td className={TD}>
        <span className={`text-tP font-semibold ${action?.tone ?? 'text-text-weak'}`}>
          {action?.text ?? '—'}
        </span>
        {/*
          Why we were wrong, when somebody told us. The flag is separate from
          the sentence because it is the part that becomes a number.
        */}
        {event.misread && (
          <span className="text-tSmall text-text-warning block">
            O sistema leu a receita errado
          </span>
        )}
        {event.reason !== null && (
          <span className="text-tSmall text-text-weak block">{event.reason}</span>
        )}
      </td>
      <td className={`${TD} whitespace-nowrap`}>{event.actor_email}</td>
      <td className={`${TD} text-text-weak whitespace-nowrap tabular-nums`}>{when(event.at)}</td>
    </tr>
  )
}

/** An absent side is an em dash, never a blank cell. */
function Side({ text }: { text: string | undefined }) {
  if (text === undefined || text === '') {
    return <span className="text-text-weak italic">—</span>
  }
  return <span>{text}</span>
}

/** Day and time, in the order a Brazilian reads them. */
function when(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  return at.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
